import { useGetSettings, useSaveSettings } from '@ais-ui/websockets';
import { useCallback } from 'react';
import {
  useSeedServiceCollectionCache,
  useUserSettingsCollectionMutations,
  useUserSettingsCollectionQuery,
} from './client';
import { useImportStatus } from './importStatus';
import { bestEffort } from './resilience';
import { settingsPhase } from './settingsPhase';
import { resolveReadSource } from './useDomainSettings';
import type { CollectionTranslator, SettingsDomain } from './types';

export interface CollectionSettingsResult<TItem extends { id: string }> {
  data: TItem[];
  isLoading: boolean;
  add: (item: TItem) => Promise<void>;
  update: (item: TItem) => Promise<void>;
  remove: (id: string) => Promise<void>;
  source: 'blob' | 'service';
}

/** Per-domain tuning for the collection engine. */
export interface CollectionDomainOptions {
  /**
   * When true, the read source is ALWAYS the blob even after import — used by
   * `wms`, whose service list endpoint returns a lossy summary (no `selected[]`,
   * a Bucket A field consumed by `olserver`). Writes still dual-write to the
   * service so it is kept in sync for a later cutover.
   */
  blobReadOnly?: boolean;
}

/**
 * Generic resilient engine for "collection" settings domains (vector_tiles,
 * radar_overlays, …). It mirrors `useDomainSettings` but for arrays:
 *
 * - The blob-derived array is ALWAYS the guaranteed fallback + read source until
 *   the app-level import has completed (phase 2) or the domain is service-only
 *   (phase 3). With `blobReadOnly`, the blob stays the read source regardless.
 * - Writes rewrite the whole blob array (authoritative through phase 2) exactly
 *   as the legacy app hooks do, then fire the matching per-item service op
 *   best-effort so a slow/absent test backend never blocks or fails a save.
 * - Login-time seeding lives in the import orchestrator, not here.
 */
export function useCollectionDomainSettings<TItem extends { id: string }>(
  domain: SettingsDomain,
  collectionPath: string,
  translator: CollectionTranslator<TItem>,
  options: CollectionDomainOptions = {},
): CollectionSettingsResult<TItem> {
  const { blobReadOnly = false } = options;
  const phase = settingsPhase();
  const usesService = phase >= 2;

  const { data: blob } = useGetSettings();
  const { mutateAsync: saveBlob } = useSaveSettings();
  const { createItem, patchItem, deleteItem } =
    useUserSettingsCollectionMutations<TItem>(domain, collectionPath);

  // The app-level import flag is the single "trust the service" signal. A
  // blob-read-only domain never reads the service, so it never needs the list.
  const { imported } = useImportStatus();
  // `blobReadOnly` suppresses SERVICE reads only while the blob is still
  // authoritative (phases 1–2). In phase 3 the service is the sole source of
  // truth, so reads AND writes both use it — otherwise phase 3 would read the
  // blob but write the service, diverging permanently.
  const serviceEnabled =
    usesService && (phase >= 3 || (!blobReadOnly && imported));
  const listQuery = useUserSettingsCollectionQuery<TItem>(
    domain,
    collectionPath,
    { enabled: serviceEnabled },
  );

  const blobItems = blob ? translator.fromBlob(blob) : undefined;
  const { data: resolved, source } = resolveReadSource<TItem[]>({
    phase,
    imported: blobReadOnly ? false : imported,
    blobData: blobItems,
    serviceData: serviceEnabled ? listQuery.data : undefined,
  });
  const items = resolved ?? [];

  const seedServiceCollectionCache = useSeedServiceCollectionCache();

  // Persist the next full array: write the blob (authoritative through phase 2)
  // and run the matching per-item service op with the right phase semantics.
  const persist = useCallback(
    async (nextItems: TItem[], serviceOp: () => Promise<unknown>) => {
      if (phase === 3) {
        await serviceOp();
        // The service list query has a 30s staleTime and no refetch-on-focus,
        // so without this the UI would keep showing the pre-write array until
        // some other trigger causes a refetch. Seed it with the just-written
        // array (mirrors the phase-2 seed below) so the change is visible
        // immediately; the next natural query lifecycle still reconciles
        // against the server.
        seedServiceCollectionCache(domain, collectionPath, nextItems);
        return;
      }
      if (blob) await saveBlob(translator.applyToBlob(blob, nextItems));
      if (phase === 2) {
        // Reads prefer the service once imported; seed its list cache from the
        // authoritative blob-derived array so the change shows even if the
        // best-effort per-item op below is dropped.
        seedServiceCollectionCache(domain, collectionPath, nextItems);
        void bestEffort(serviceOp(), (error) =>
          console.error(
            `Best-effort collection write to user-settings service for "${domain}" failed:`,
            error,
          ),
        );
      }
    },
    [
      phase,
      blob,
      saveBlob,
      translator,
      domain,
      collectionPath,
      seedServiceCollectionCache,
    ],
  );

  // Base array to diff writes against. Phase 1-2: always the blob's OWN items,
  // never the resolved read list — once imported, reads prefer the service,
  // which may return a trimmed summary (e.g. wms drops `selected[]`), and
  // rewriting the authoritative blob from that summary would permanently drop
  // those fields. Phase 3: the service IS the source of truth and the blob is
  // no longer written to at all, so diffs must be based on the resolved
  // (service-preferred) items instead of a blob that may be stale or absent.
  const writeItems = phase >= 3 ? items : (blobItems ?? items);

  // These close over the item arrays, which are derived fresh each render, so
  // memoizing them would buy no referential stability — keep them as plain
  // functions.
  const add = (item: TItem) =>
    persist([...writeItems, item], () => createItem(item));

  const update = (item: TItem) =>
    persist(
      writeItems.map((existing) => (existing.id === item.id ? item : existing)),
      () => patchItem(item.id, item),
    );

  const remove = (id: string) =>
    persist(
      writeItems.filter((existing) => existing.id !== id),
      () => deleteItem(id),
    );

  const isLoading = blob ? false : serviceEnabled ? listQuery.isLoading : true;

  return { data: items, isLoading, add, update, remove, source };
}
